// frontend/src/components/ETADisplay/CompletedTripView.js
// Component for displaying completed trip with actual vs estimated comparison
import { CheckCircle, UserPlus, Flag, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { formatTimeLocal, formatDistance, formatDuration } from '../../utils/etaFormatters';
import { isPreferenceEnabled } from '../../utils/etaWaypointHelpers';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';

const CompletedTripView = ({
    displayData,
    relevantStops,
    isDriver,
    showWaypoints,
    setShowWaypoints,
    pickupStopId,
    dropoffStopId,
    getTranslatedWaypointName,
    handleOpenBoardingInfo,
    hasBoardingInfo,
    entitySettings,
    currentEntity,
    entityTimeZone = null
}) => {
    const { t } = useTranslation();
    const { formatTime, formatDateTime, distanceUnit } = useDateTimePreferences();
    const { text, background, border, components, cn } = useTheme();

    const showComparison = displayData.type === 'completed_comparison' && isDriver;
    const showApiEstimate = isPreferenceEnabled(
        entitySettings?.show_api_estimate_comparison ??
        currentEntity?.show_api_estimate_comparison ??
        currentEntity?.preferences?.show_api_estimate_comparison ??
        currentEntity?.entity_settings?.show_api_estimate_comparison
    ) && isDriver;
    const hasApiEstimates = displayData.waypoints.some(wp => wp.api_estimated_arrival_time);
    console.log("[CompletedTripView] API Estimate Debug:", {
        showApiEstimate,
        hasApiEstimates,
        isDriver,
        preference: entitySettings?.show_api_estimate_comparison,
        waypointsWithApi: displayData.waypoints.filter(wp => wp.api_estimated_arrival_time).length,
        firstApiEstimate: displayData.waypoints.find(wp => wp.api_estimated_arrival_time)?.api_estimated_arrival_time
    });
    return (
        <div className={cn(
            "rounded-lg border overflow-hidden",
            components.card.base
        )}>
            <div className={cn(
                "px-4 py-3 border-b flex items-center justify-between",
                background.secondary
            )}>
                <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <h3 className={cn("font-medium", text.primary)}>
                        {showComparison ? t('Trip Completed - Estimate vs Actual') : t('Trip Completed - Actual Times')}
                    </h3>
                </div>

                {isDriver && displayData.waypoints && displayData.waypoints.length > 1 && (
                    <button
                        onClick={() => setShowWaypoints(!showWaypoints)}
                        className={cn(
                            "text-sm focus:outline-none",
                            text.link,
                            text.linkHover
                        )}
                    >
                        {showWaypoints ? t('Hide Route Details') : t('Show Route Details')}
                    </button>
                )}
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead className={cn(background.secondary, border.default, "border-b")}>
                        <tr>
                            <th className={cn("px-4 py-2 text-left", text.primary)}>{t('Location')}</th>
                            <th className={cn("px-4 py-2 text-right", text.primary)}>{t('Segment')}</th>
                            <th className={cn("px-4 py-2 text-right", text.primary)}>{t('Stay')}</th>
                            <th className={cn("px-4 py-2 text-right", text.primary)}>{t('Total')}</th>
                            <th className={cn("px-4 py-2 text-right", text.primary)}>{t('Actual Time')}</th>
                            {showComparison && <th className={cn("px-4 py-2 text-right", text.primary)}>{t('Planned Time')}</th>}
                            {showComparison && <th className={cn("px-4 py-2 text-right", text.primary)}>{t('Variance')}</th>}
                            {showApiEstimate && hasApiEstimates && <th className={cn("px-4 py-2 text-right", text.primary)}>{t('GPS Estimate')}</th>}
                            {showApiEstimate && hasApiEstimates && <th className={cn("px-4 py-2 text-right", text.primary)}>{t('GPS Variance')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {relevantStops.map((waypoint, index) => {
                            const isUserPickup = (!isDriver && waypoint.waypoint_id === pickupStopId) || waypoint.is_user_pickup;
                            const isUserDropoff = (!isDriver && waypoint.waypoint_id === dropoffStopId) || waypoint.is_user_dropoff;
                            const isUserPrimaryStop = isUserPickup || isUserDropoff;
                            const stopIsPickup = waypoint.can_pickup === 1 || waypoint.can_pickup === true;
                            const stopIsDropoff = waypoint.can_dropoff === 1 || waypoint.can_dropoff === true;
                            const canShowBoardingInfo = waypoint.point_type === 'stop'
                                && hasBoardingInfo(waypoint)
                                && (isDriver || isUserPrimaryStop);

                            // A stop is skipped when backend marks is_skipped=true (not first, not last)
                            const isSkipped = waypoint.is_skipped === true
                                && index !== 0
                                && index !== relevantStops.length - 1;

                            return (
                                <tr key={waypoint.waypoint_id || index} className={cn(
                                    "border-b",
                                    border.default,
                                    isSkipped && "opacity-50"
                                )}>
                                    <td className="px-4 py-2">
                                        <div className="flex items-center">
                                            <div className="w-3 h-3 rounded-full mr-2 bg-gray-400"></div>
                                            <div className="flex items-center gap-2 flex-grow">
                                                <div>
                                                    <span className={isUserPrimaryStop ? 'font-medium' : ''}>
                                                        {getTranslatedWaypointName(waypoint.waypoint_id, waypoint.waypoint_name || waypoint.name)}
                                                        {isSkipped && <span className={cn("text-xs font-normal italic ml-1", text.disabled)}>({waypoint.is_manual_skip ? t('manuallySkipped') : t('noRidersSkipped')})</span>}
                                                        {isUserPickup && <span className={cn("text-xs font-normal ml-1", text.muted)}>({t('Your Pickup')})</span>}
                                                        {isUserDropoff && <span className={cn("text-xs font-normal ml-1", text.muted)}>({t('Your Dropoff')})</span>}
                                                    </span>
                                                    {/* Status badges removed for completed trips - no longer relevant */}

                                                    {isDriver && (
                                                        <div className="text-xs text-gray-600 mt-1 flex items-center gap-2">
                                                            {stopIsPickup && (waypoint.pickup_riders > 0 || waypoint.pickup_luggages > 0) && (
                                                                <span className={cn("flex items-center", text.secondary)}>
                                                                    <UserPlus className="h-3 w-3 mr-1" />
                                                                    {t('pickup')}: {waypoint.pickup_riders || 0} {t('riders')}, {waypoint.pickup_luggages || 0} {t('bags')}
                                                                </span>
                                                            )}
                                                            {stopIsDropoff && (waypoint.dropoff_riders > 0 || waypoint.dropoff_luggages > 0) && (
                                                                <span className={cn("flex items-center", text.secondary)}>
                                                                    <Flag className="h-3 w-3 mr-1" />
                                                                    {t('dropoff')}: {waypoint.dropoff_riders || 0} {t('riders')}, {waypoint.dropoff_luggages || 0} {t('bags')}
                                                                </span>
                                                            )}
                                                            {!stopIsPickup && !stopIsDropoff && (
                                                                <span className="text-gray-500">
                                                                    {t('Transit Stop')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Boarding Info Badge */}
                                                    {canShowBoardingInfo && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenBoardingInfo(waypoint);
                                                            }}
                                                            className={cn(
                                                                "inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-xs font-medium rounded transition-colors",
                                                                "bg-blue-100 dark:bg-blue-900/30",
                                                                "text-blue-700 dark:text-blue-300",
                                                                "hover:bg-blue-200 dark:hover:bg-blue-900/50"
                                                            )}
                                                            title={t('boardingInfoAvailable')}
                                                        >
                                                            <MapPin className="h-3 w-3" />
                                                            {t('findLocation')}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        {isSkipped ? '—' : (
                                            <>
                                                <div>
                                                    {formatDistance(waypoint.dynamic_distance_meters, distanceUnit, t)}
                                                    {waypoint.is_segment_distance_planned && (
                                                        <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                                                            {t('planned')}
                                                        </span>
                                                    )}
                                                </div>
                                                {waypoint.dynamic_duration_minutes > 0 && (
                                                    <div className="text-xs text-gray-500">{formatDuration(waypoint.dynamic_duration_minutes, t)}</div>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        {isSkipped ? '—' : formatDuration(waypoint.stay_duration_minutes, t)}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        {isSkipped ? '—' : (
                                            <>
                                                <div>{formatDistance(waypoint.gross_dynamic_distance_meters, distanceUnit, t)}</div>
                                                {waypoint.gross_dynamic_duration_minutes > 0 && (
                                                    <div className="text-xs text-gray-500">{formatDuration(waypoint.gross_dynamic_duration_minutes, t)}</div>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        {isSkipped ? '—' : (formatTimeLocal(waypoint.actual_arrival_time, formatTime, formatDateTime, t, entityTimeZone) || '—')}
                                    </td>
                                    {showComparison && (
                                        <td className="px-4 py-2 text-right">
                                            {isSkipped ? '—' : formatTimeLocal(waypoint.estimated_arrival_time, formatTime, formatDateTime, t, entityTimeZone)}
                                        </td>
                                    )}
                                    {showComparison && (
                                        <td className="px-4 py-2 text-right">
                                            {isSkipped ? '—' : (waypoint.variance_minutes !== null && waypoint.variance_minutes !== undefined ?
                                                <span>
                                                    {waypoint.variance_minutes > 0 ? '+' : ''}
                                                    {waypoint.variance_minutes} {t('min')}
                                                </span> :
                                                '—')}
                                        </td>
                                    )}
                                    {showApiEstimate && hasApiEstimates && (
                                        <td className="px-4 py-2 text-right">
                                            {isSkipped ? '—' : (waypoint.api_estimated_arrival_time ? formatTimeLocal(waypoint.api_estimated_arrival_time, formatTime, formatDateTime, t, entityTimeZone) : '—')}
                                        </td>
                                    )}
                                    {showApiEstimate && hasApiEstimates && (
                                        <td className="px-4 py-2 text-right">
                                            {isSkipped ? '—' : (waypoint.api_variance_minutes !== null && waypoint.api_variance_minutes !== undefined ?
                                                <span>
                                                    {waypoint.api_variance_minutes > 0 ? '+' : ''}
                                                    {waypoint.api_variance_minutes} {t('min')}
                                                </span> :
                                                '—')}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CompletedTripView;
